/**
 * Juror-specific voir dire generation + side-aware suitability assessment.
 *
 * Crosses the CHARGED CRIME (category labels from the charge→research map)
 * with THIS JUROR'S actual questionnaire answers to produce questions that
 * name both — not generic research boilerplate — and an assessment of the
 * juror's suitability argued from the user's side (defense or prosecution).
 *
 * Pure functions (no DOM, no DB) so the whole layer is unit-testable. All
 * inferences are probabilistic signals for lawful, non-discriminatory
 * selection; nothing here reads or weighs protected characteristics.
 */
import type { Juror } from '@/types';
import type { Side } from '@/types/state';

/** A charged crime, as parsed from the Case tab's charge list. */
export interface ChargeInfo {
  chapter: string;
  section: string;
  /** Human description from the charge text, e.g. "MURDER". */
  name: string;
  /** Charge-category labels from the research map (e.g. "Murder / homicide"). */
  labels: string[];
}

/** One generated question with the reason it applies to this juror. */
export interface SpecificQuestion {
  question: string;
  /** Why this question fires: the juror answer + charge feature that triggered it. */
  basis: string;
}

/** Charge-category feature detection over the mapped research labels. */
export interface ChargeFeatures {
  violent: boolean;
  sexual: boolean;
  child: boolean;
  domestic: boolean;
  homicide: boolean;
  policeCentral: boolean;
  substance: boolean;
  financial: boolean;
  insanityLikely: boolean;
  names: string[];
}

/** Derive charge features from category labels + charge names. */
export function chargeFeatures(charges: ChargeInfo[]): ChargeFeatures {
  const all = charges.flatMap((c) => [...c.labels, c.name]).join(' | ');
  return {
    violent: /murder|homicide|manslaughter|assault|battery|robbery|rape|sexual|strangulation|kidnap|home invasion|carjack|arson|mayhem/i.test(all),
    sexual: /rape|sexual|indecent|child sexual|SORB|sexually dangerous|prostitution|trafficking/i.test(all),
    child: /child|minor/i.test(all),
    domestic: /domestic|abuse.prevention|209A|harassment order|strangulation/i.test(all),
    homicide: /murder|homicide|manslaughter/i.test(all),
    policeCentral: /police|resisting|drug|firearm|OUI|impaired|weapon/i.test(all),
    substance: /OUI|impaired|drug|alcohol/i.test(all),
    financial: /larceny|fraud|embezzlement|white.collar|forgery|corruption|bribery|gaming|usury/i.test(all),
    insanityLikely: /insanity|diminished/i.test(all),
    names: [...new Set(charges.map((c) => c.name.toLowerCase()).filter(Boolean))]
  };
}

const yes = (v: string | undefined): boolean => !!v && /^yes/i.test(v.trim());
const stated = (v: string | undefined): boolean =>
  !!v && !/^(no|none|none stated|not found|—|n\/a)$/i.test(v.trim());

/** Human phrase for the charged crime(s), e.g. "murder" / "murder and strangulation". */
export function chargePhrase(f: ChargeFeatures): string {
  if (!f.names.length) return 'the crime charged';
  return f.names.join(' and ');
}

/**
 * Instantiate a bracketed template against an actual answer: picks the
 * matching option from "[law enforcement/law/social services]" when the
 * answer implies one; otherwise substitutes the answer itself.
 */
export function fillTemplate(template: string, answer: string): string {
  return template.replace(/\[([^\]]+)\]/g, (_m, opts: string) => {
    const options = opts.split('/').map((o) => o.trim());
    const a = answer.toLowerCase();
    const hit = options.find((o) => a.includes(o.toLowerCase()) || o.toLowerCase().includes(a));
    if (hit) return hit;
    // Domain heuristics for common placeholder sets.
    if (/police|sheriff|trooper|corrections|probation/i.test(answer) && options.some((o) => /law enforcement/i.test(o)))
      return options.find((o) => /law enforcement/i.test(o)) as string;
    return answer || options[0];
  });
}

/**
 * Generate questions specific to THIS juror and THIS charge: each rule fires
 * only when a juror answer and a charge feature intersect, and the question
 * names both. Ordered by importance; deduped by the caller.
 */
export function specificQuestions(j: Juror, charges: ChargeInfo[]): SpecificQuestion[] {
  const f = chargeFeatures(charges);
  const crime = chargePhrase(f);
  const out: SpecificQuestion[] = [];
  const q = (question: string, basis: string): void => {
    out.push({ question, basis });
  };
  const spouseOcc = String(j.questionnaire?.['spouse_partner_occupation'] ?? '');
  const spouseBiz = String(j.questionnaire?.['spouse_type_of_business'] ?? '');
  const describe = String(j.questionnaire?.['experience_with_law_describe'] ?? '');

  // ── Victimization × charge ─────────────────────────────────────────────
  if (yes(j.victim)) {
    const violentVictim = /violent/i.test(j.victim);
    if (f.homicide || (f.violent && violentVictim)) {
      q(
        `You indicated you or someone close to you was the victim of a ${violentVictim ? 'violent crime' : 'crime'}. This case involves a charge of ${crime}. Would that experience come to mind as you heard evidence about violence, and could you set it aside and decide only on what is proved in this courtroom?`,
        `Crime victim ("${j.victim}") × ${crime}`
      );
    } else {
      q(
        `You noted a past experience as a crime victim. Is there anything about a ${crime} case in particular that would make that experience weigh on you here?`,
        `Crime victim ("${j.victim}") × ${crime}`
      );
    }
    if (f.sexual) {
      q(
        `Some of the evidence in a ${crime} case can be difficult to hear. Given your own experience, would you be able to evaluate testimony about sexual allegations fairly to both sides?`,
        `Crime victim × sexual-offense charge`
      );
    }
  }

  // ── Law-enforcement connection × police-witness credibility ────────────
  if (stated(j.le)) {
    q(
      `You indicated a connection to law enforcement (${j.le}). Police witnesses are likely to testify about the ${crime} charge. Would you tend to give an officer's testimony more weight, less weight, or the same weight as any other witness, before hearing it?`,
      `LE connection ("${j.le}") × police testimony in a ${crime} case`
    );
  }
  if (/police|sheriff|trooper|corrections|probation|law enforcement/i.test(spouseOcc + ' ' + spouseBiz)) {
    q(
      `Your spouse or partner works in law enforcement (${spouseOcc || spouseBiz}). In a ${crime} case the police investigation itself may be challenged. Would you feel you were second-guessing your household if you voted not guilty based on doubts about the investigation?`,
      `Spouse/partner in law enforcement × ${crime} investigation`
    );
  }

  // ── Accused/family history × prosecution fairness ──────────────────────
  if (yes(j.accused)) {
    q(
      `You indicated that you or someone close to you has been accused of a crime. Would anything about that experience make it hard to sit in judgment on a ${crime} charge, or leave you leaning for or against the Commonwealth before the evidence starts?`,
      `Prior accusation ("${j.accused}") × ${crime}`
    );
  }

  // ── Parent of minor children × child-involved charges ─────────────────
  if (stated(j.parent) && /minor/i.test(j.parent) && (f.child || f.sexual)) {
    q(
      `You are a parent of minor children, and this case involves ${crime}. Many parents tell us a case like this is simply too close to home. Would you be able to hold the Commonwealth to its burden and acquit if the evidence fell short?`,
      `Parent of minors × child-involved charge`
    );
  }

  // ── Domestic-violence charges × household experience ──────────────────
  if (f.domestic) {
    q(
      `This case involves ${crime} arising in a domestic setting. Without telling us anything private, is there anything in your own life or in the lives of people close to you that would make a domestic case hard for you to hear fairly?`,
      `Domestic-violence charge — universal screen`
    );
  }

  // ── Occupation crossings ───────────────────────────────────────────────
  if (/nurse|doctor|physician|emt|paramedic|medical|health/i.test(j.occ || '') && (f.violent || f.homicide)) {
    q(
      `As a ${j.occ}, you may have seen injuries like those the evidence will describe. Would your professional experience lead you to fill in gaps in the medical testimony with your own expertise rather than deciding on the evidence presented?`,
      `Medical occupation ("${j.occ}") × injury evidence in a ${crime} case`
    );
  }
  if (/nurse|doctor|physician|psych|counsel|therap|social work|mental/i.test(j.occ || '') && f.insanityLikely) {
    q(
      `A defense in this ${crime} case may involve the defendant's mental state, with expert testimony on both sides. Given your background as a ${j.occ}, could you weigh the experts' opinions on the evidence rather than substituting your own professional judgment?`,
      `Clinical occupation × mental-state defense`
    );
  }
  if (/teacher|educat|childcare|daycare|pediatric/i.test(j.occ || '') && (f.child || f.sexual)) {
    q(
      `Your work brings you into contact with children, and this case charges ${crime}. Would that make it difficult to presume the defendant innocent from the first day to the last?`,
      `Child-facing occupation ("${j.occ}") × child-involved charge`
    );
  }
  if (/driver|truck|delivery|uber|lyft|transport/i.test(j.occ || '') && f.substance) {
    q(
      `You drive for a living, and this case involves ${crime}. Do you have views about impaired driving that would make it hard to hold the Commonwealth to proof beyond a reasonable doubt?`,
      `Professional driver × impaired-operation charge`
    );
  }
  if (/bank|financ|account|audit|invest/i.test((j.occ || '') + ' ' + (j.bizType || '')) && f.financial) {
    q(
      `Your work involves finance, and this case charges ${crime}. Would you expect yourself to re-analyze the records with your own training, or can you decide on the analysis presented by the witnesses?`,
      `Financial occupation × financial charge`
    );
  }

  // ── Substance-related charges × household experience ──────────────────
  if (f.substance) {
    q(
      `Cases involving ${crime} sometimes touch lives through a family member's experience with alcohol or drugs. Without details, is there anything of that kind that would make this case hard for you to judge on the evidence alone?`,
      `Substance-related charge — universal screen`
    );
  }

  // ── Prior jury service × this charge ───────────────────────────────────
  if (yes(j.jury)) {
    q(
      `You have served on a jury before. Was that a criminal or a civil case, did the jury reach a verdict, and is there anything about that experience that would affect how you approach a ${crime} charge?`,
      `Prior jury service × ${crime}`
    );
  }

  // ── Case knowledge / formed opinion ────────────────────────────────────
  if (stated(j.caseKnow) || stated(j.opinion)) {
    q(
      `You indicated you have heard something about this case${stated(j.opinion) ? ' and may have formed an impression' : ''}. If the evidence at trial turned out different from what you heard, could you follow the evidence — and would you commit to deciding this ${crime} charge only on what happens in this courtroom?`,
      `Pretrial exposure ("${j.caseKnow || j.opinion}") × ${crime}`
    );
  }

  // ── Hedged impartiality — cause-challenge builder ──────────────────────
  if (j.fair && !/yes/i.test(j.fair) && stated(j.fair)) {
    q(
      `On your questionnaire you did not state outright that you could be fair ("${j.fair}"). Sitting here now, knowing the charge is ${crime}, do you honestly doubt your ability to be impartial? There is no wrong answer.`,
      `Hedged impartiality ("${j.fair}") — cause-challenge foundation`
    );
  }

  // ── Free-text law experience mentioning police/courts ─────────────────
  if (/arrest|police|court|charge|convict/i.test(describe)) {
    q(
      `You described an experience with the legal system on your questionnaire. Did that experience leave you with views about police or prosecutors that could carry into a ${crime} trial?`,
      `Law-experience narrative × ${crime}`
    );
  }

  return out;
}

/** A side-aware suitability assessment for the juror detail. */
export interface SuitabilityAssessment {
  /** Favorability of this juror for the user's client. */
  lean: 'favorable' | 'unfavorable' | 'neutral' | 'high-risk-unknown';
  headline: string;
  /** Concrete, juror-specific reasons (answer × charge), for the user's side. */
  points: string[];
  /** What to pin down in voir dire before spending a strike / accepting. */
  probes: string[];
}

/** Research angles relevant to the charge, keyed by whether this juror triggers them. */
export interface AngleInput {
  topic: string;
  angle: string; // the current side's angle text from the research entry
}

/**
 * Assess this juror's suitability for the user's client, considering the
 * charge thoroughly: numeric score, side-tagged flags, and the charge-mapped
 * research angles that this juror's answers actually trigger.
 */
export function assessSuitability(
  j: Juror,
  side: Side,
  score: number,
  flags: Array<{ label: string; side: string }>,
  charges: ChargeInfo[],
  angles: AngleInput[]
): SuitabilityAssessment {
  const f = chargeFeatures(charges);
  const crime = chargePhrase(f);
  const mySide = side === 'defense' ? 'def' : 'pros';
  const forMe = flags.filter((x) => x.side === mySide || x.side === side);
  const against = flags.filter((x) => x.side !== mySide && x.side !== side);

  // Score is signed toward the defense in this codebase's convention
  // (positive = defense-favorable); flip for the prosecution's view.
  const myScore = side === 'defense' ? score : -score;

  const points: string[] = [];
  const probes: string[] = [];

  // Charge-specific risk answers regardless of score.
  const hedgedFair = !!j.fair && !/yes/i.test(j.fair) && stated(j.fair);
  const victimHit = yes(j.victim) && f.violent;
  const leHit = stated(j.le);

  if (victimHit)
    points.push(
      side === 'prosecution'
        ? `Victim experience ("${j.victim}") often aligns with the Commonwealth in a ${crime} case — but it can cut either way if the experience involved dissatisfaction with police or courts.`
        : `Victim experience ("${j.victim}") is a defense risk in a ${crime} case — probe whether the memory would surface during graphic or emotional testimony.`
    );
  if (leHit)
    points.push(
      side === 'prosecution'
        ? `Law-enforcement connection ("${j.le}") tends to credit officer testimony, which matters where police witnesses carry the ${crime} case.`
        : `Law-enforcement connection ("${j.le}") is a defense concern where the ${crime} case turns on police testimony; build the record on witness-weighing.`
    );
  if (hedgedFair)
    points.push(
      `Impartiality answer is hedged ("${j.fair}") — a potential challenge for cause, which spends no peremptory either way.`
    );
  if (stated(j.caseKnow) || stated(j.opinion))
    points.push(`Pretrial exposure noted ("${j.caseKnow || j.opinion}") — in a ${crime} case, exposure plus an opinion is cause material.`);

  // Side-tagged flags, framed for the client.
  for (const fl of forMe.slice(0, 3)) points.push(`Cuts for your client: ${fl.label}.`);
  for (const fl of against.slice(0, 3)) points.push(`Cuts against your client: ${fl.label}.`);

  // Charge-mapped research angles this juror actually triggers.
  for (const a of angles.slice(0, 3)) points.push(`${a.topic}: ${a.angle}`);

  // Probes: what to resolve before deciding.
  if (victimHit) probes.push('Pin down whether the victim experience involved similar facts and how it resolved.');
  if (leHit) probes.push('Commit them on equal weighing of officer vs. civilian testimony.');
  if (hedgedFair) probes.push('Develop the hedged-impartiality answer toward a cause challenge before using a peremptory.');
  if (yes(j.jury)) probes.push('Ask whether the prior jury reached a verdict and in what kind of case.');
  if (!probes.length) probes.push(`No charge-specific red flags from the questionnaire — use open-ended questions about attitudes toward ${crime} to surface unknowns.`);

  let lean: SuitabilityAssessment['lean'];
  if (hedgedFair) lean = 'high-risk-unknown';
  else if (myScore >= 3 && against.length === 0) lean = 'favorable';
  else if (myScore <= -3 || against.length >= 2) lean = 'unfavorable';
  else lean = 'neutral';

  const sideName = side === 'defense' ? 'the defense' : 'the Commonwealth';
  const headline =
    lean === 'favorable'
      ? `Leans favorable for ${sideName} on a ${crime} charge — verify with the probes below before accepting.`
      : lean === 'unfavorable'
        ? `Leans unfavorable for ${sideName} on a ${crime} charge — a strike candidate; confirm the basis in voir dire (never a protected characteristic).`
        : lean === 'high-risk-unknown'
          ? `Impartiality is unresolved — develop for cause before spending a peremptory.`
          : `No strong lean for ${sideName} on a ${crime} charge — suitability turns on the probes below.`;

  return { lean, headline, points, probes };
}
